import { Box } from '@mui/material';
import type { MouseEvent, ReactElement } from 'react';

import { CohortsListActionMenu } from '@verantos-dev/feature-cohort-list';
import type { Cohort } from '@verantos-dev/types-cohorts';

import type { ProjectArtifact, ProjectArtifactKind } from '../types';
import { AnalysisRowActions } from './AnalysisRowActions';
import { AnalyticalDatasetRowActions } from './AnalyticalDatasetRowActions';
import { ConceptGroupRowActions } from './ConceptGroupRowActions';
import { VariableRowActions } from './VariableRowActions';

// Kinds whose row actions are a single Delete/Rename-style menu that owns
// its own dialog — each renders identically (see the shared `Box` wrapper
// below), only the inner component differs.
const SIMPLE_ROW_ACTIONS: Partial<
  Record<ProjectArtifactKind, (artifact: ProjectArtifact, projectId: string) => ReactElement>
> = {
  analytical_dataset: (artifact) => <AnalyticalDatasetRowActions artifact={artifact} />,
  concept_group: (artifact) => <ConceptGroupRowActions artifact={artifact} />,
  analysis: (artifact) => <AnalysisRowActions artifact={artifact} />,
  // OR-6090: creator-only Rename. Renders null for non-owner rows (no
  // kebab shown), same shape as the others.
  variable: (artifact, projectId) => (
    <VariableRowActions artifact={artifact} projectId={projectId} />
  ),
};

export interface ProjectArtifactRowActionsProps {
  artifact: ProjectArtifact;
  projectId: string;
}

/**
 * Build the partial `Cohort` `CohortsListActionMenu` needs from the manifest
 * scalars on `kindSpecific`. The menu only reads id / name / status /
 * latestSubjectCount / latestCohortVersionId / isExportInProgress /
 * createdBy.userId — all of which the enriched manifest already returns, so no
 * per-row fetch is needed. Cast to `Cohort` because the menu consumes only
 * this subset.
 */
const toMenuCohort = (artifact: ProjectArtifact): Cohort => {
  const ks = artifact.kindSpecific;
  return {
    cohortProfileId: artifact.id,
    cohortName: artifact.name,
    latestStatus: artifact.status,
    latestCohortVersionId: (ks?.latestCohortVersionId as string | null) ?? '',
    latestSubjectCount: (ks?.latestSubjectCount as number | null) ?? 0,
    isExportInProgress: Boolean(ks?.isExportInProgress),
    createdBy: { userId: (ks?.createdByUserId as string | null) ?? artifact.owner.id },
  } as Cohort;
};

/**
 * Per-row action menu for the project-artifacts datagrid. Cohorts get the full
 * menu (rename/copy/export/delete via `CohortsListActionMenu`, driven by the
 * cohort modal context provider); analytical datasets get a creator-only Delete
 * (`AnalyticalDatasetRowActions`, which owns its own delete modal); concept
 * groups get a permission-gated Delete (`ConceptGroupRowActions`, same
 * pattern); analyses get a creator-only Delete (`AnalysisRowActions`). Other
 * kinds render nothing until their action surfaces are wired up.
 *
 * Everything the menu needs travels on the manifest row (enriched JOIN), so
 * the menu mounts synchronously — no snapshot fetch.
 */
export const ProjectArtifactRowActions = ({
  artifact,
  projectId,
}: ProjectArtifactRowActionsProps) => {
  const stopPropagation = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const renderSimpleActions = SIMPLE_ROW_ACTIONS[artifact.kind];
  if (renderSimpleActions) {
    return (
      <Box
        onClick={stopPropagation}
        data-testid={`project-artifact-row-actions-${artifact.id}`}
        sx={{ display: 'inline-flex' }}
      >
        {renderSimpleActions(artifact, projectId)}
      </Box>
    );
  }

  if (artifact.kind !== 'cohort') return null;

  return (
    <Box data-testid={`cohort-row-actions-${artifact.id}`} sx={{ display: 'contents' }}>
      <Box
        onClick={stopPropagation}
        data-testid={`project-artifact-row-actions-${artifact.id}`}
        sx={{ display: 'inline-flex' }}
      >
        <CohortsListActionMenu cohort={toMenuCohort(artifact)} />
      </Box>
    </Box>
  );
};
